/**
 * FieldDiff — one before/after row inside an UpdateBody. The cell
 * renderer chosen for the value type matches what the Step's
 * DataTable would render so a diff row reads visually like the
 * cell it edits.
 *
 *   levelBadge   → faded LevelBadge → LevelBadge swap
 *   categoryPill → faded CategoryPill → CategoryPill swap
 *   refs / chips → strip with unchanged + removed + added
 *   numeric      → tabular old → tabular new
 *   text / fallback → unified diff lines with - / + markers
 *
 * The Item Definition agent (`analysis_agent`) has no entry in
 * AGENT_COLUMNS — its field paths are dotted (e.g.
 * `system_overview.purpose`) — so it falls through to the text-diff
 * branch, which reads naturally for prose form fields.
 */
import React from 'react';
import LevelBadge from '../../../../components/ui/LevelBadge/LevelBadge';
import CategoryPill from '../../../../components/ui/CategoryPill/CategoryPill';
import { AGENT_COLUMNS } from '../../utils/operationMeta';
import { resolveLevelColor } from '../../utils/severityResolver';
import { FieldChange } from '../../state/types';
import styles from './FieldDiff.module.css';

interface FieldDiffProps {
  agentName: string;
  change: FieldChange;
}

export default function FieldDiff({ agentName, change }: FieldDiffProps) {
  const col = AGENT_COLUMNS[agentName]?.find((c) => c.key === change.field);
  const type = col?.type ?? 'text';
  const label = col?.label ?? change.field;

  if (type === 'levelBadge') {
    const oldColor = resolveLevelColor(agentName, change.field, change.old_value);
    const newColor = resolveLevelColor(agentName, change.field, change.new_value);
    return (
      <div className={styles.row}>
        <div className={styles.fieldName}>{label}</div>
        <div className={styles.swap}>
          <span className={styles.faded}>
            <LevelBadge color={oldColor} label={String(change.old_value)} size="sm" />
          </span>
          <span className={styles.swapArrow}>→</span>
          <LevelBadge color={newColor} label={String(change.new_value)} size="sm" />
        </div>
      </div>
    );
  }

  if (type === 'categoryPill') {
    return (
      <div className={styles.row}>
        <div className={styles.fieldName}>{label}</div>
        <div className={styles.swap}>
          <span className={styles.faded}>
            <CategoryPill label={String(change.old_value)} size="sm" />
          </span>
          <span className={styles.swapArrow}>→</span>
          <CategoryPill label={String(change.new_value)} size="sm" />
        </div>
      </div>
    );
  }

  if (type === 'chips' || type === 'refs') {
    const oldArr = Array.isArray(change.old_value)
      ? (change.old_value as unknown[])
      : [];
    const newArr = Array.isArray(change.new_value)
      ? (change.new_value as unknown[])
      : [];
    const removed = oldArr.filter((v) => !newArr.includes(v));
    const added = newArr.filter((v) => !oldArr.includes(v));
    const unchanged = oldArr.filter((v) => newArr.includes(v));
    const mono = type === 'refs';
    const chipBase = `${styles.chip} ${mono ? styles.chipMono : ''}`;
    return (
      <div className={styles.row}>
        <div className={styles.fieldName}>{label}</div>
        <div className={styles.chipStrip}>
          {unchanged.map((v) => (
            <span key={`u-${String(v)}`} className={`${chipBase} ${styles.chipUnchanged}`}>
              {String(v)}
            </span>
          ))}
          {removed.map((v) => (
            <span key={`r-${String(v)}`} className={`${chipBase} ${styles.chipRemoved}`}>
              {String(v)}
            </span>
          ))}
          {added.map((v) => (
            <span key={`a-${String(v)}`} className={`${chipBase} ${styles.chipAdded}`}>
              {String(v)}
            </span>
          ))}
        </div>
      </div>
    );
  }

  if (type === 'numeric') {
    return (
      <div className={styles.row}>
        <div className={styles.fieldName}>{label}</div>
        <div className={styles.swap}>
          <span className={`${styles.numChange} ${styles.numRemoved}`}>
            {String(change.old_value)}
          </span>
          <span className={styles.swapArrow}>→</span>
          <span className={`${styles.numChange} ${styles.numAdded}`}>
            {String(change.new_value)}
          </span>
        </div>
      </div>
    );
  }

  // text / longText / unknown → unified two-line diff.
  return (
    <div className={styles.rowStacked}>
      <div className={styles.fieldName}>{label}</div>
      <div className={styles.textDiff}>
        <div className={`${styles.diffLine} ${styles.remove}`}>
          <span className={styles.diffMark}>−</span>
          <span className={styles.diffText}>{stringify(change.old_value)}</span>
        </div>
        <div className={`${styles.diffLine} ${styles.add}`}>
          <span className={styles.diffMark}>+</span>
          <span className={styles.diffText}>{stringify(change.new_value)}</span>
        </div>
      </div>
    </div>
  );
}

function stringify(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}
